import { zodResolver } from "@hookform/resolvers/zod";
import { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";

import {
  type OptionItem,
  OptionType,
  useGetCityByCountrysQuery,
  useGetOptionsQuery,
} from "@/api/admin";
import { branchesApi } from "@/api/admin/branches";
import { useGetBranchAvailableLegalEntityByBrandIdQuery } from "@/api/admin/branches/useBranchHooks";
import {
  type BaseEntityLocation,
  useGetLocationDataIdQuery,
} from "@/api/admin/locations";
import { RESPONSE_ANSWERS } from "@/api/constants";
import {
  CustomButton,
  CustomCardBody,
  CustomLabel,
  LabelWithOptional,
  Loader,
} from "@/components/atoms";
import {
  BasicModal,
  InputControl,
  PhoneTagsInput,
  SearchableSelectControl,
  SelectControl,
  TextareaControl,
  toastError,
  toastSuccess,
  toastWarning,
} from "@/components/molecules";
import { Form, FormField, FormItem } from "@/components/ui/form";
import { cn } from "@/lib/utils";
import type { DemoBranchStore } from "@/modules/BranchStepsAdmin/DemoBranchStore";
import { useWizardStepDraft } from "@/modules/wizardDraft";
import type { FormProps, FormRefType } from "@/types/common";
import { getDefaultPhoneCode } from "@/utils/getDefaultPhoneCode";
import { getUniquePhoneCodeItems } from "@/utils/getUniquePhoneCodeItems";
import { useExposeFormValues } from "@/utils/hooks/useExposeFormValues";
import { isIdInTree, mapToTreeData } from "@/utils/mapToTreeData";
import { parseE164 } from "@/utils/parseE164";

import { BranchDetailsSchema, type BranchDetailsType } from "./types";

const SAUDI_ARABI_PHONE_CODE = "+966";
const SAUDI_COUNTRY_CODE = "SA";

export type BrandSelectionMode = {
  canChangeBrand: boolean;
  showBrandSelection: boolean;
};

interface BranchDetailsFormProps extends FormProps<BranchDetailsType> {
  canChangeBrand?: boolean;
  showBrandSelection?: boolean;
  branchStore: DemoBranchStore;
  draftEntityId?: string;
}

const BranchDetailsForm = forwardRef<
  FormRefType<BranchDetailsType>,
  BranchDetailsFormProps
>(
  (
    {
      defaultValues,
      onSubmit,
      onValidityChange,
      branchStore,
      id,
      classes = "",
      canChangeBrand = false,
      showBrandSelection = false,
      draftEntityId,
    },
    ref
  ) => {
    const phoneCodeSelectItems = useMemo(() => getUniquePhoneCodeItems(), []);
    const brandId = defaultValues?.brandId;

    const [isVerifyMessage, setIsVerifyMessage] = useState<string | null>(null);
    const [isFetchingGoogleData, setIsFetchingGoogleData] = useState(false);
    const [phoneCodeDisabled, setPhoneCodeDisabled] = useState(true);
    const [isFetchingGoogleSummary, setIsFetchingGoogleSummary] =
      useState(false);

    const form = useForm<BranchDetailsType>({
      resolver: zodResolver(BranchDetailsSchema),
      defaultValues: {
        ...defaultValues,
        brandId: brandId || defaultValues?.brandId || "",
      },
      mode: "onChange",
      reValidateMode: "onBlur",
    });

    useWizardStepDraft({
      form,
      wizard: "branch",
      entityId: draftEntityId,
      step: "details",
    });

    const {
      control,
      handleSubmit,
      watch,
      trigger,
      formState: { isValid, dirtyFields },
      reset,
    } = form;

    const {
      data: optionsData,
      isPending: isOptionPending,
      isSuccess,
    } = useGetOptionsQuery([OptionType.Brands, OptionType.Countries]);

    const watchedCityId = watch("cityId") || "";

    const { data: locationData, isPending: isLocationPending } =
      useGetLocationDataIdQuery(watchedCityId, !!watchedCityId);

    const subFieldsAddressPlaceholder = isLocationPending
      ? "Select city first"
      : "Select";

    useEffect(() => {
      if (watchedCityId && dirtyFields.cityId && !isInitialMountRef.current) {
        form.setValue("areaId", "");
        form.setValue("streetId", "");
        form.setValue("landmarkId", "");
      }
    }, [watchedCityId, dirtyFields.cityId]);

    const mappedLocation = useMemo(() => {
      if (!locationData) {
        return { areas: [], streets: [], landmarks: [] };
      }

      const toOption = (item: BaseEntityLocation): OptionItem => ({
        id: item.id,
        name: item.nameEn,
      });

      return {
        areas: locationData.areas.map(toOption),
        streets: locationData.streets.map(toOption),
        landmarks: locationData.landmarks.map(toOption),
      };
    }, [locationData]);

    useEffect(() => {
      if (!locationData || !defaultValues) return;

      const currentArea = form.getValues("areaId");
      const currentStreet = form.getValues("streetId");
      const currentLandmark = form.getValues("landmarkId");

      if (defaultValues.areaId && !currentArea) {
        form.setValue("areaId", defaultValues.areaId, { shouldValidate: true });
      }

      if (defaultValues.streetId && !currentStreet) {
        form.setValue("streetId", defaultValues.streetId, {
          shouldValidate: true,
        });
      }

      if (defaultValues.landmarkId && !currentLandmark) {
        form.setValue("landmarkId", defaultValues.landmarkId, {
          shouldValidate: true,
        });
      }
    }, [locationData]);

    // Watch brandId from form to reload legal entities when it changes
    const watchedBrandId = watch("brandId");
    const currentBrandId = watchedBrandId || brandId || defaultValues?.brandId;

    const { data: legalEntitiesData, isPending: isLegalEntitiesPending } =
      useGetBranchAvailableLegalEntityByBrandIdQuery(currentBrandId || "");

    const treeData = mapToTreeData(legalEntitiesData ?? []);

    // Track the branch ID to detect when we're loading a different branch
    const lastBranchIdRef = useRef<string | undefined>(defaultValues?.id);
    const isInitialMountRef = useRef(true);

    // Only reset form when:
    // 1. Initial mount and options are loaded
    // 2. Branch ID changes (loading a different branch)
    // This prevents resetting when user types (which updates store and causes re-render)
    useEffect(() => {
      if (defaultValues && optionsData && isSuccess) {
        const currentBranchId = defaultValues.id;
        const shouldReset =
          isInitialMountRef.current ||
          lastBranchIdRef.current !== currentBranchId;

        if (shouldReset) {
          reset(
            {
              ...defaultValues,
              brandId: brandId || defaultValues?.brandId || "",
            },
            { keepErrors: true }
          );
          lastBranchIdRef.current = currentBranchId;
          isInitialMountRef.current = false;
        }
      }
    }, [
      defaultValues?.id,
      optionsData,
      isSuccess,
      reset,
      brandId,
      showBrandSelection,
    ]);

    const isInvalidId = isVerifyMessage === RESPONSE_ANSWERS.INVALID_ID;

    const [googlePhoneCodeModal, setGooglePhoneCodeModal] = useState<{
      newCode: string;
      oldCode: string;
    } | null>(null);

    const handleRegenerate = async () => {
      const googlePlaceIdValue = form.getValues("googlePlaceId");
      if (!googlePlaceIdValue?.trim()) return;

      setIsFetchingGoogleSummary(true);

      try {
        const summary =
          await branchesApi.getBranchByGooglePlaceId(googlePlaceIdValue);

        form.setValue("reviewSummaryEn", summary.reviewSummaryEn ?? "", {
          shouldValidate: true,
        });
        form.setValue("reviewSummaryAr", summary.reviewSummaryAr ?? "", {
          shouldValidate: true,
        });

        branchStore?.setDetails({
          ...branchStore.details,
          reviewSummaryEn: summary.reviewSummaryEn || "",
          reviewSummaryAr: summary.reviewSummaryAr || "",
        });

        toastSuccess("Google reviews summary was updated.");
      } catch {
        toastError("Failed to fetch Google summary", handleRegenerate, "Error");
      } finally {
        setIsFetchingGoogleSummary(false);
      }
    };

    const handleVerifyBusiness = async () => {
      const googlePlaceIdValue = form.getValues("googlePlaceId");

      if (!googlePlaceIdValue?.trim()) return;

      setIsFetchingGoogleData(true);
      setIsVerifyMessage(null);

      try {
        const branchData =
          await branchesApi.getBranchByGooglePlaceId(googlePlaceIdValue);

        const parsed = parseE164(branchData.phone || "");
        const fallbackPhoneCode =
          getDefaultPhoneCode() ?? SAUDI_ARABI_PHONE_CODE;
        const currentPhoneCode = form.getValues("phoneCode");

        const googlePhoneCode = parsed?.phoneCode ?? fallbackPhoneCode;
        const hasPhoneFromGoogle = !!parsed?.phoneNumber;

        const isMismatch =
          hasPhoneFromGoogle &&
          !!currentBrandId &&
          googlePhoneCode &&
          currentPhoneCode &&
          googlePhoneCode !== currentPhoneCode;

        if (isMismatch && hasPhoneFromGoogle) {
          setGooglePhoneCodeModal({
            oldCode: currentPhoneCode,
            newCode: googlePhoneCode,
          });
        }

        let phoneCodeToSet: string;

        if (hasPhoneFromGoogle) {
          phoneCodeToSet = isMismatch ? currentPhoneCode : googlePhoneCode;
        } else {
          const matchedCountry = optionsData?.countries?.find(
            c => c.id === defaultValues.brandCountry
          );
          phoneCodeToSet = matchedCountry?.phoneCode ?? fallbackPhoneCode;
        }

        const newValues: BranchDetailsType = {
          ...form.getValues(),
          nameEn: branchData.nameEn ?? "",
          nameAr: branchData.nameAr ?? "",
          descriptionEn: branchData.descriptionEn ?? "",
          descriptionAr: branchData.descriptionAr ?? "",
          phoneCode: phoneCodeToSet,
          phoneNumber: parsed?.phoneNumber ?? "",
          addressEn: branchData.addressEn ?? "",
          addressAr: branchData.addressAr ?? "",
          longitude: branchData.longitude,
          latitude: branchData.latitude,
          googleTotalReviews: branchData.googleTotalReviews,
          googlePlaceAverageRating: branchData.googlePlaceAverageRating,
          reviewSummaryEn: branchData.reviewSummaryEn ?? "",
          reviewSummaryAr: branchData.reviewSummaryAr ?? "",
        };

        form.reset(newValues, {
          keepErrors: true,
          keepDirty: true,
          keepTouched: true,
        });

        form.trigger();

        branchStore?.updateBranchCoreFields({
          ...branchData,
          phoneCode: phoneCodeToSet,
        });

        setIsVerifyMessage(branchData.nameEn);
      } catch {
        setIsVerifyMessage(RESPONSE_ANSWERS.INVALID_ID);
        toastError("Failed to verify Google Place ID", handleVerifyBusiness);
      } finally {
        setIsFetchingGoogleData(false);
      }
    };

    useEffect(() => {
      onValidityChange?.(isValid);
    }, [isValid, onValidityChange]);

    useExposeFormValues(ref, form);

    // Watch legal entity ID and clear it if it's not in the tree
    const watchedLegalEntityId = watch("legalEntityId");

    useEffect(() => {
      // Skip if tree is still loading
      if (isLegalEntitiesPending) {
        return;
      }

      // Skip on initial mount to avoid clearing valid default values
      if (isInitialMountRef.current) {
        return;
      }

      // If a legal entity is selected but not in the tree, clear it
      if (watchedLegalEntityId && !isIdInTree(watchedLegalEntityId, treeData)) {
        form.setValue("legalEntityId", undefined);
      }
    }, [watchedLegalEntityId, treeData, isLegalEntitiesPending, form]);

    const phoneCode = useWatch({ control: form.control, name: "phoneCode" });
    const phoneNumber = useWatch({
      control: form.control,
      name: "phoneNumber",
    });

    useEffect(() => {
      if (phoneNumber?.trim()) {
        trigger("phoneNumber");
      }
    }, [phoneCode, phoneNumber, trigger]);

    useEffect(() => {
      if (defaultValues?.phoneCode) {
        form.setValue("phoneCode", defaultValues.phoneCode, {
          shouldValidate: true,
          shouldDirty: false,
        });
        setPhoneCodeDisabled(true);
      }
    }, [defaultValues.id]);

    const prevBrandIdRef = useRef<string | undefined>(undefined);
    const isEditModeRef = useRef<boolean>(!!defaultValues?.id);

    useEffect(() => {
      if (!optionsData?.countries) return;

      const fallbackPhoneCode = getDefaultPhoneCode() ?? SAUDI_ARABI_PHONE_CODE;
      const isEditMode = !!defaultValues?.id;
      const hasSavedPhoneCode = !!defaultValues?.phoneCode;

      if (isEditMode && hasSavedPhoneCode) {
        form.setValue("phoneCode", defaultValues.phoneCode, {
          shouldValidate: true,
          shouldDirty: false,
        });

        let isMounted = true;

        const setDisabledFromBrand = async () => {
          try {
            if (!isMounted) return;

            const matchedCountry = optionsData?.countries?.find(
              c => c.id === defaultValues.brandCountry
            );

            setPhoneCodeDisabled(!!matchedCountry?.phoneCode);
          } catch {
            if (!isMounted) return;
            setPhoneCodeDisabled(false);
          }
        };

        setDisabledFromBrand();

        return () => {
          isMounted = false;
        };
      }

      if (!currentBrandId) {
        form.setValue("phoneCode", fallbackPhoneCode, {
          shouldValidate: true,
        });
        setPhoneCodeDisabled(false);
        return;
      }

      let isMounted = true;
      const initFromBrand = async () => {
        try {
          if (!isMounted) return;

          const matchedCountry = optionsData?.countries?.find(
            c => c.id === defaultValues.brandCountry
          );

          const brandPhoneCode = matchedCountry?.phoneCode ?? fallbackPhoneCode;

          const currentPhoneCode = form.getValues("phoneCode");
          const phoneNumber = form.getValues("phoneNumber");

          const brandChanged = prevBrandIdRef.current !== currentBrandId;

          const hasUserPhoneData = !!phoneNumber || !!currentPhoneCode;

          const shouldShowToast =
            !isEditModeRef.current &&
            brandChanged &&
            hasUserPhoneData &&
            currentPhoneCode &&
            !defaultValues.brandId &&
            !defaultValues.referenceNumber &&
            currentPhoneCode !== brandPhoneCode;

          form.setValue("phoneCode", brandPhoneCode, {
            shouldValidate: true,
          });

          setPhoneCodeDisabled(!!matchedCountry?.phoneCode);

          if (shouldShowToast) {
            toastWarning(
              "Phone code was changed according to the brand's country"
            );
          }

          prevBrandIdRef.current = currentBrandId;
        } catch {
          if (!isMounted) return;
          form.setValue("phoneCode", fallbackPhoneCode, {
            shouldValidate: true,
          });
          setPhoneCodeDisabled(false);
        }
      };

      initFromBrand();

      return () => {
        isMounted = false;
      };
    }, [currentBrandId, optionsData?.countries, defaultValues?.id]);

    const isRegenerateAvailable =
      !form.getValues("googlePlaceId") ||
      form.getValues("googlePlaceId")?.trim().length === 0;

    const { data: citiesData } = useGetCityByCountrysQuery(SAUDI_COUNTRY_CODE);

    if (isOptionPending || !optionsData || !isSuccess) {
      return <Loader fullscreen />;
    }

    return (
      <>
        <Form {...form}>
          <form
            onSubmit={handleSubmit(onSubmit)}
            id={id}
            data-testid="branch-details-section-basic"
          >
            <CustomCardBody classes={cn("pb-6", classes)}>
              <div className="mb-[-13px]">
                <div className="parent">
                  <div className={cn("responsive-fields")}>
                    {showBrandSelection && (
                      <div className="flex flex-col gap-1 mb-5.5">
                        <CustomLabel htmlFor="brandName" label="Brand" />
                        <SelectControl<BranchDetailsType>
                          form={form}
                          nameSelect="brandId"
                          placeholder="Select"
                          itemsList={optionsData?.brands || []}
                          triggerProps={{
                            "data-testid": "branch-details-brand-trigger",
                          }}
                          disabled={!canChangeBrand}
                        />
                      </div>
                    )}

                    <InputControl<BranchDetailsType>
                      formControl={control}
                      label="Reference number"
                      nameField="referenceNumber"
                      disabled
                      classedName={cn(
                        showBrandSelection ? "order-3 mb-5.5" : "mb-5.5"
                      )}
                      data-testid="branch-details-reference"
                    />

                    <InputControl<BranchDetailsType>
                      formControl={control}
                      label="Branch name (English)"
                      nameField="nameEn"
                      data-testid="branch-details-name-english"
                    />
                    <InputControl<BranchDetailsType>
                      formControl={control}
                      label="Branch name (Arabic)"
                      nameField="nameAr"
                      data-testid="branch-details-name-arabic"
                      dir="rtl"
                    />
                  </div>
                </div>

                <div className="parent">
                  <div className="responsive-fields">
                    <div className="flex flex-col gap-1 text-sm mb-5.5">
                      <LabelWithOptional label="Legal entity" />
                      <SelectControl<BranchDetailsType>
                        form={form}
                        nameSelect="legalEntityId"
                        placeholder="Select"
                        itemsList={treeData}
                        triggerProps={{
                          "data-testid": "brand-form-details-legal-entity",
                        }}
                        isTree
                        disabled={!currentBrandId || isLegalEntitiesPending}
                      />
                    </div>

                    <div className="flex flex-col gap- text-sm mb-5.5">
                      <InputControl<BranchDetailsType>
                        formControl={control}
                        label={
                          <p className="pb-1">
                            Google place ID
                            <span className="text-gray-300 pl-1">
                              (optional)
                            </span>
                          </p>
                        }
                        classedName="gap-0"
                        nameField="googlePlaceId"
                        data-testid="brand-form-details-place-id"
                        disabled={isFetchingGoogleData}
                        infoMessage={
                          isVerifyMessage &&
                          (isInvalidId ? (
                            <span className="text-red-500 font-bold">
                              Invalid ID
                            </span>
                          ) : (
                            <span>
                              Business name is
                              <span className="font-bold">
                                &quot;{isVerifyMessage}&quot;
                              </span>
                            </span>
                          ))
                        }
                        isError={isInvalidId}
                        extraButton={{
                          text: isFetchingGoogleData ? "Loading" : "Verify",
                          onClick: handleVerifyBusiness,
                          testId: "clear-google-business-id",
                        }}
                      />
                    </div>
                  </div>
                </div>

                <div className="parent">
                  <div className="responsive-fields">
                    <div className="flex flex-col gap-1 text-sm">
                      <LabelWithOptional label="Menu URL" />
                      <InputControl<BranchDetailsType>
                        formControl={control}
                        nameField="menuUrl"
                        data-testid="brand-form-details-menu-url"
                      />
                    </div>

                    <div className="flex flex-col gap-1 text-sm">
                      <LabelWithOptional label="Reservation URL" />
                      <InputControl<BranchDetailsType>
                        formControl={control}
                        nameField="bookingUrl"
                        data-testid="brand-form-details-reservation-url"
                      />
                    </div>
                  </div>
                </div>

                <div className="parent">
                  <div className="responsive-fields-grid gap-0 md:gap-5">
                    <TextareaControl<BranchDetailsType>
                      formControl={control}
                      label="Description (English)"
                      nameField="descriptionEn"
                      data-testid="branch-details-description-en"
                    />

                    <TextareaControl<BranchDetailsType>
                      formControl={control}
                      label="Description (Arabic)"
                      nameField="descriptionAr"
                      data-testid="branch-details-description-ar"
                      dir="rtl"
                    />
                  </div>
                </div>

                <div className="parent">
                  <div className="responsive-fields">
                    <div className="flex flex-col gap-1 justify-end">
                      <CustomLabel htmlFor="phoneCode" label="Phone number" />
                      <div className="flex gap-3">
                        <SearchableSelectControl
                          form={form}
                          name="phoneCode"
                          items={phoneCodeSelectItems}
                          className="w-24"
                          disabled={phoneCodeDisabled}
                        />
                        <InputControl<BranchDetailsType>
                          formControl={control}
                          nameField="phoneNumber"
                          data-testid="branch-details-phone-number"
                          classedName="gap-0 mb-5.5"
                        />
                      </div>
                    </div>
                    <div className="flex flex-col text-sm">
                      <LabelWithOptional label="SMS phone numbers" />
                      <FormField
                        control={form.control}
                        name="smsPhoneNumbers"
                        render={() => (
                          <FormItem className="relative">
                            <PhoneTagsInput
                              name="smsPhoneNumbers"
                              data-testid="branch-details-phone-number-sms"
                              classes="mb-5.5"
                            />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>
                </div>

                <div className="parent">
                  <div className="responsive-fields">
                    <InputControl<BranchDetailsType>
                      formControl={control}
                      label="Address (English)"
                      nameField="addressEn"
                      data-testid="branch-details-address-en"
                    />
                    <InputControl<BranchDetailsType>
                      formControl={control}
                      label="Address (Arabic)"
                      nameField="addressAr"
                      data-testid="branch-details-address-ar"
                      dir="rtl"
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-5.5 mb-5.5">
                  <div className="flex gap-5.5 flex-wrap sm:flex-nowrap">
                    <div className="flex flex-col w-full sm:w-1/2 gap-1 text-sm">
                      <CustomLabel htmlFor="cityId" label="City" />
                      <SelectControl<BranchDetailsType>
                        form={form}
                        nameSelect="cityId"
                        placeholder="Select city"
                        itemsList={citiesData || []}
                        triggerProps={{
                          "data-testid": "branch-form-city-trigger",
                        }}
                      />
                    </div>
                    <div className="flex flex-col w-full sm:w-1/2 gap-1 text-sm">
                      <LabelWithOptional label="Area" />
                      <SelectControl<BranchDetailsType>
                        form={form}
                        nameSelect="areaId"
                        isSearchable
                        placeholder={subFieldsAddressPlaceholder}
                        itemsList={mappedLocation.areas || []}
                        disabled={isLocationPending || !watchedCityId}
                        triggerProps={{
                          "data-testid": "branch-details-area-trigger",
                        }}
                      />
                    </div>
                  </div>

                  <div className="flex gap-5.5 flex-wrap sm:flex-nowrap">
                    <div className="flex w-full sm:w-1/2 flex-col gap-1 text-sm">
                      <LabelWithOptional label="Street" />
                      <SelectControl<BranchDetailsType>
                        form={form}
                        nameSelect="streetId"
                        isSearchable
                        placeholder={subFieldsAddressPlaceholder}
                        itemsList={mappedLocation.streets || []}
                        disabled={isLocationPending || !watchedCityId}
                        triggerProps={{
                          "data-testid": "branch-details-street-trigger",
                        }}
                      />
                    </div>
                    <div className="flex flex-col w-full sm:w-1/2 gap-1 text-sm">
                      <LabelWithOptional label="Landmark" />
                      <SelectControl<BranchDetailsType>
                        form={form}
                        nameSelect="landmarkId"
                        isSearchable
                        placeholder={subFieldsAddressPlaceholder}
                        itemsList={mappedLocation.landmarks || []}
                        disabled={isLocationPending || !watchedCityId}
                        triggerProps={{
                          "data-testid": "branch-details-landmark-trigger",
                        }}
                      />
                    </div>
                  </div>
                </div>

                <div className="parent mb-1">
                  <div className="responsive-fields-grid gap-x-5">
                    <TextareaControl<BranchDetailsType>
                      formControl={control}
                      label="Google Reviews Summary (English)"
                      nameField="reviewSummaryEn"
                      data-testid="branch-google-reviews-en"
                    />

                    <TextareaControl<BranchDetailsType>
                      formControl={control}
                      label="Google Reviews Summary (Arabic)"
                      nameField="reviewSummaryAr"
                      data-testid="branch-google-reviews-ar"
                      dir="rtl"
                    />
                  </div>
                </div>
                <div className="relative group">
                  <CustomButton
                    type="button"
                    variant="blue"
                    className={cn(
                      "bg-blue-50 border border-blue-600 mb-5 hover:bg-blue-100 mb-11 disabled:bg-gray-300 disabled:border-gray-500 disabled:text-gray-800",
                      isFetchingGoogleSummary &&
                        "bg-gray-300 border-gray-500 hover:bg-gray-300"
                    )}
                    full
                    onClick={handleRegenerate}
                    data-testid="regenerate-button"
                    isPending={isFetchingGoogleSummary}
                    disabled={isRegenerateAvailable}
                  >
                    Re-generate
                  </CustomButton>
                  <p
                    className={cn(
                      "flex flex-col absolute left-1/2 -top-13 transform -translate-x-1/2 bg-gray-600 text-white text-sm px-2 py-1 rounded pointer-events-none transition-opacity duration-200 opacity-0",
                      isRegenerateAvailable && "group-hover:opacity-100"
                    )}
                  >
                    <span>This feature is not available.</span>
                    <span>Please enter your Google Place ID first.</span>
                  </p>
                </div>
              </div>
            </CustomCardBody>
          </form>
        </Form>
        {googlePhoneCodeModal && (
          <BasicModal
            isOpen={true}
            onOpenChange={() => setGooglePhoneCodeModal(null)}
            title="Phone code mismatch"
            body={
              <p>
                The phone code retrieved from Google differs from the brand’s
                country. Are you sure you want to overwrite the current phone
                code?
              </p>
            }
            btnTitle="Yes, overwrite"
            isPending={isFetchingGoogleData}
            action={() => {
              if (!googlePhoneCodeModal) return;

              const { newCode } = googlePhoneCodeModal;
              form.setValue("phoneCode", newCode, { shouldValidate: true });

              branchStore?.setDetails({
                ...branchStore.details,
                phoneCode: newCode,
              });

              setGooglePhoneCodeModal(null);
            }}
          />
        )}
      </>
    );
  }
);

BranchDetailsForm.displayName = "BranchDetailsForm";
export default BranchDetailsForm;
